import { useCallback, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { ClientsService } from "../services/clientsService";
import type { ClientByIdResponse } from "../types/clients-api.types";
import { displayClientName } from "../utils/clientsTableHelpers";
import { DeleteEntityDialogs } from "./DeleteEntityDialogs";
import { EntityDangerZone } from "./EntityDangerZone";

export interface DeleteOrganizationSectionProps {
  /** The organization in client-detail shape (see `useOrganizationKycProfile`). */
  organization: ClientByIdResponse;
  /** Fired after the organization is gone — the host should leave this page. */
  onDeleted: () => void;
}

/**
 * Deletes an organization (`DELETE /internal/organizations/:id`) from its own detail page. This used to live
 * inside the client detail drawer, where the target was whatever entity the drawer had loaded; it now sits on
 * the page whose subject IS the organization. Permission gating (`canDeleteOrganizations`) is the host's.
 */
export function DeleteOrganizationSection({ organization, onDeleted }: DeleteOrganizationSectionProps) {
  const { t } = useTranslations("clients");
  const [deleteStep, setDeleteStep] = useState<"first" | "final" | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleCancel = useCallback(() => {
    if (deleting) return;
    setDeleteStep(null);
  }, [deleting]);

  const handleConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await ClientsService.deleteOrganization(organization.id);
      colocaToast.success(t("view_toast_delete_organization_success"));
      setDeleteStep(null);
      onDeleted();
    } catch (err) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("view_toast_delete_organization_error"));
    } finally {
      setDeleting(false);
    }
  }, [organization.id, onDeleted, t]);

  return (
    <>
      <EntityDangerZone
        variant="organization"
        entityName={displayClientName(organization)}
        onAction={() => setDeleteStep("first")}
        disabled={deleting}
      />

      <DeleteEntityDialogs
        entity={organization}
        variant="organization"
        step={deleteStep}
        deleting={deleting}
        onCancel={handleCancel}
        onProceed={() => setDeleteStep("final")}
        onConfirm={handleConfirm}
      />
    </>
  );
}
